import {
  AfterContentChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { of, Subject } from 'rxjs';
import {
  catchError,
  map,
  startWith,
  switchMap,
  takeUntil,
} from 'rxjs/operators';
import { GeneralHelper } from 'src/app/helpers/general-helper';
import { SubProject } from 'src/app/model/project/sub-project';
import { EmployeeChoicesService } from 'src/app/services/employee-choices.service';
import { ProductTypeCriteriumService } from 'src/app/services/product-type-criterium-service';
import { MostRecentPlacementControllerRequestService } from './most-recent-placement-controller-request-service';
import { SubscriptionWithDestroyTriggerComponent } from 'src/app/subscription-helpers/subscription-with-destroy-trigger.component';
import { ProperPageResult } from 'src/app/model/proper-page-result';
import MostRecentPlacementHelper, {
  SubProjectDisplay,
} from './most-recent-placement-helper';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import Employee from 'src/app/model/product/employee';

@Component({
  selector: 'most-recent-placement-overview',
  templateUrl: './most-recent-placement-overview.component.html',
  styleUrls: ['./most-recent-placement-overview.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MostRecentPlacementOverviewComponent
  extends SubscriptionWithDestroyTriggerComponent
  implements OnInit, OnDestroy, AfterContentChecked
{
  private static CHUNK_SIZE = 5;

  subProjectDisplays: SubProjectDisplay[] = [];

  renderedStream: Subject<SubProjectDisplay[]> = new Subject();
  private rendering: boolean = false;
  private rendered: SubProjectDisplay[] = [];

  loadingSubProjects: boolean = false;
  loadingPlacers: boolean = true;

  reloadData: Subject<any> = new Subject();

  placerOptions: Employee[];
  selectedPlacer?: Employee;

  constructor(
    private mostRecentPlacementControllerRequestService: MostRecentPlacementControllerRequestService,
    private employeeChoicesService: EmployeeChoicesService,
    private productTypeCriteriumService: ProductTypeCriteriumService,
    private cd: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.employeeChoicesService.updatePlacerChoices();
    this.initializeSubProjectSubscription();
    this.initializePlacerSubscription();
    this.initializeProductTypeCriteriumChangeSubscription();
  }

  private initializeSubProjectSubscription(): void {
    this.addSubscription(
      this.reloadData.pipe(
        startWith({}),
        switchMap(() => {
          this.loadingSubProjects = true;
          this.cd.markForCheck();
          const typeCriteriumParam =
            this.productTypeCriteriumService.generateTypeCriteriumParam();
          return this.mostRecentPlacementControllerRequestService.getPage(
            this.selectedPlacer == null ? null : this.selectedPlacer.id,
            typeCriteriumParam == null ? null : typeCriteriumParam[0]
          );
        }),
        map((data: ProperPageResult<SubProject>) => {
          return data.items;
        }),
        catchError(() => {
          return of([]);
        }),
        takeUntil(this.destroyThisComponent)
      ),
      (subProjects: SubProject[]) => {
        const newDisplays: SubProjectDisplay[] = [];
        if (CollectionHelper.isPopulated(subProjects)) {
          subProjects.forEach((sp) =>
            MostRecentPlacementHelper.addSubProjectDisplays(sp, newDisplays)
          );
        }
        this.subProjectDisplays = newDisplays;
        this.loadingSubProjects = false;
        this.startRendering();
      }
    );
  }

  private initializePlacerSubscription(): void {
    this.addSubscription(
      this.employeeChoicesService.getPlacerChoicesSubscribable(),
      (placers: Employee[]) => {
        this.placerOptions = placers;
        if (CollectionHelper.isPopulated(this.placerOptions)) {
          this.loadingPlacers = false;
        }
        this.cd.markForCheck();
      }
    );
  }

  private initializeProductTypeCriteriumChangeSubscription(): void {
    this.addSubscription(
      this.productTypeCriteriumService.getTypeCriteriumChangedSubscribable(),
      () => this.reloadData.next()
    );
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.renderedStream.complete();
    this.reloadData.complete();
  }

  ngAfterContentChecked(): void {
    if (this.rendering) {
      this.renderNextChunk();
    }
  }

  selectedPlacerChange(): void {
    this.reloadData.next();
  }

  private startRendering(): void {
    if (!this.rendering) {
      this.rendered.length = 0;
      this.rendering = true;
      this.cd.markForCheck();
    }
  }

  private renderNextChunk(): void {
    if (this.rendered.length >= this.subProjectDisplays.length) {
      if (this.subProjectDisplays.length === 0) {
        this.rendered.length = 0;
        this.renderedStream.next(this.rendered);
      }
      this.rendering = false;
      return;
    }

    this.rendered = [
      ...this.rendered,
      ...this.subProjectDisplays.slice(
        this.rendered.length,
        Math.min(
          this.subProjectDisplays.length,
          this.subProjectDisplays.length +
            MostRecentPlacementOverviewComponent.CHUNK_SIZE
        )
      ),
    ];
    this.renderedStream.next(this.rendered);
    setTimeout(() => {
      this.cd.detectChanges();
    }, 0);
  }

  compareEntities(o1: any, o2: any) {
    return GeneralHelper.compareEntities(o1, o2);
  }

  trackById(index: number, obj: any): number {
    return GeneralHelper.trackById(index, obj);
  }
}
